/**
 * One FAQ builder for the /local page and its JSON-LD. Rates are supplied by
 * the server from state_energy_rates, including DC; no competing static copy.
 */

import { buildFactualAttribution } from '@/lib/citation-patterns'
import { familySizeAnswer, recommendedCuFtFor } from '@/lib/refrigerator-answer-facts'
import { FIS_CANONICAL_DEFINITION, FIS_SCOPE_SUMMARY, FIS_DIRECTION_BY_SCOPE, FIS_LOCATION_COMPONENT_SENTENCE, FIS_ZIP_COMPONENT_SENTENCE } from '@/lib/fis-factors'

export interface LocalIndexFaq {
  question: string
  answer: string
}

export interface LocalIndexState {
  code: string
  name: string
  /** Residential electricity rate, cents per kWh (EIA). */
  rate: number
  /** Verified source-period wording computed on the server. */
  sourceCredit?: string
}

/** Explicit example consumption, not a measured catalog or household average. */
export const TYPICAL_ENERGY_STAR_KWH = 450

export interface LocalIndexStateStats {
  cheapest: LocalIndexState[]
  expensive: LocalIndexState[]
  cheapestState: LocalIndexState
  expensiveState: LocalIndexState
  secondExpensiveState: LocalIndexState
  /** Unweighted mean of the supplied state/DC rates, ¢/kWh, two decimals as a string. */
  avgRate: string
  /** Annual cost of TYPICAL_ENERGY_STAR_KWH at the mean rate, whole dollars. */
  avgAnnualCost: number
  cheapestAnnualCost: number
  expensiveAnnualCost: number
}

const annualCostAt = (rateCents: number) => Math.round((TYPICAL_ENERGY_STAR_KWH * rateCents) / 100)

/** Pure arithmetic over the rates rendered alongside these answers. */
export function computeLocalIndexStateStats(states: readonly LocalIndexState[]): LocalIndexStateStats | null {
  if (states.length < 2 || states.some(state => !Number.isFinite(state.rate) || state.rate <= 0) ||
      new Set(states.map(state => state.code)).size !== states.length) return null
  const sorted = [...states].sort((a, b) => a.rate - b.rate)
  const avgRate = states.reduce((sum, s) => sum + s.rate, 0) / states.length
  return {
    cheapest: sorted.slice(0, 5),
    expensive: sorted.slice(-5).reverse(),
    cheapestState: sorted[0],
    expensiveState: sorted[sorted.length - 1],
    secondExpensiveState: sorted[sorted.length - 2],
    avgRate: avgRate.toFixed(2),
    avgAnnualCost: annualCostAt(avgRate),
    cheapestAnnualCost: annualCostAt(sorted[0].rate),
    expensiveAnnualCost: annualCostAt(sorted[sorted.length - 1].rate),
  }
}

const perKwh = (rateCents: number) => `$${(rateCents / 100).toFixed(2)}/kWh`
const listStates = (states: LocalIndexState[]) => states.map((s) => `${s.name} (${perKwh(s.rate)})`).join(', ')

/**
 * The index FAQ — identical on the page and in the FAQPage JSON-LD.
 * Order: local-intent questions, then FIS, then the rate/cost set built from the table.
 */
export function buildLocalIndexFaqs(states: readonly LocalIndexState[] = []): LocalIndexFaq[] {
  const s = computeLocalIndexStateStats(states)
  const spread = s ? s.expensiveAnnualCost - s.cheapestAnnualCost : null
  return [
    {
      question: 'How do I find refrigerators near me?',
      answer:
        'Fridge.com helps you compare refrigerator offers using local electricity-rate context and supported FIS scores. Enter your ZIP code to see its associated utility-average estimate; state and city pages use the state residential average. Model-specific running costs appear when reported annual electricity consumption is available. Confirm your tariff, product details, and delivery availability with the utility and retailer.',
    },
    {
      question: 'Where can I find freezers near me?',
      answer:
        buildFactualAttribution('Starting with your location helps you compare freezer options using local climate and electricity-rate context.', 'local-index:freezers-near-me') +
        ' ZIP pages show a utility-average estimate, which may differ from your tariff. Compare chest freezers, upright freezers, and garage-ready freezers by size, type, and features, then confirm delivery and placement requirements with the retailer.',
    },
    {
      question: 'How do I compare refrigerator prices near me?',
      answer:
        'Fridge.com compares available refrigerator offers across major online appliance retailers. After entering your ZIP code, browse listed prices and model-specific running-cost estimates where reported annual consumption is available. Those estimates use the displayed area-average rate, not your household tariff. Consider purchase price, delivery, actual energy use, and expected ownership when comparing total cost.',
    },
    {
      question: 'How do I find refrigerator and freezer deals near me?',
      answer:
        buildFactualAttribution('Comparing listed offers across retailers can help you find refrigerator and freezer deals.', 'local-index:deals-near-me') +
        ' Sort by price and compare product specifications, including reported annual energy use where available. The area-average electricity rate shown here supports estimates; your tariff and household usage determine your actual running cost. Verify the final price and delivery availability with the retailer.',
    },
    // FRIDGE-225: state the 0–100 scale (definitional, durable), never observed
    // min/max endpoints — those drift with every data refresh (measured
    // 08/01/2026: cities 20–90, ZIPs 70–98; the old "45 to 99" matched neither).
    // Re-derive from fis_locations/zip_energy_data before ever re-adding endpoints.
    // The direction sentence is imported, never retyped: /local spans states,
    // cities AND ZIPs, and the two scopes are scored by inverted formulas, so
    // any single-direction claim here is false for one of them.
    {
      question: 'What is the FIS (Fridge.com Intelligence Score)?',
      answer:
        FIS_CANONICAL_DEFINITION + ' ' + FIS_SCOPE_SUMMARY + ' ' + FIS_DIRECTION_BY_SCOPE,
    },
    // Item-109(7) ruling (07/20/2026): the FIS is described by its four real
    // inputs only. The pseudo-factors ("Grid Efficiency", "Nutritional
    // Velocity", "Kitchen Economics", "Climate Impact", "Humidity Levels")
    // were never inputs and are ruled out of FIS language.
    {
      question: 'How is the FIS calculated?',
      answer:
        FIS_LOCATION_COMPONENT_SENTENCE + ' ' + FIS_ZIP_COMPONENT_SENTENCE + ' ' + FIS_DIRECTION_BY_SCOPE,
    },
    {
      question: 'Does climate affect refrigerator efficiency?',
      answer:
        'Yes. In hot climates a refrigerator works harder to hold its internal temperature and uses more electricity; high humidity means more defrost cycles and extra load on the compressor; cold climates can stop a standard refrigerator cycling properly in an unheated garage. The FIS score at Fridge.com accounts for the climate zone, and we recommend garage-ready models for extreme temperature environments.',
    },
    {
      question: 'What refrigerators are best for hot or humid climates?',
      answer:
        'The best refrigerators for hot or humid climates are: (1) models with high-efficiency compressors that handle heat stress, (2) garage-ready refrigerators rated for roughly 38°F–110°F ambient temperatures, (3) units with humidity-controlled crisper drawers for food preservation, and (4) Energy Star certified models that maintain efficiency under load. Enter your ZIP code to see recommendations tailored to your specific climate zone.',
    },
    {
      question: 'Can I put a refrigerator in my garage?',
      answer:
        'Only with the right model. Garage placement calls for a garage-ready refrigerator, which manufacturers rate for ambient temperatures of roughly 38°F to 110°F; a standard refrigerator can stop cycling in a cold garage or overwork in a hot one. Your state and city pages on Fridge.com say whether the local climate zone favors garage placement.',
    },
    {
      question: 'What size refrigerator do I need for my family?',
      // FRIDGE-1435: the household sizes are the size calculator's own
      // recommendations (lib/refrigerator-answer-facts.ts), the same answer the
      // homepage and /llms.txt give, never a separately typed range.
      answer:
        `A common rule of thumb is 4–6 cubic feet of refrigerator space per adult in your household. ${familySizeAnswer(4)} For one or two people it recommends about ${recommendedCuFtFor(1)}–${recommendedCuFtFor(2)} cu. ft., and for five or more about ${recommendedCuFtFor(5)} cu. ft. and up. Also factor in your shopping habits — weekly bulk shoppers need more space than daily shoppers. French door and side-by-side styles offer different space configurations for the same total capacity.`,
    },
    ...(s ? [{
      question: 'Is an Energy Star refrigerator worth the extra cost?',
      answer: `Usually, yes. ENERGY STAR certified refrigerators use at least 9% less energy than the federal minimum standard, and the difference is worth most where electricity is expensive. Using an example refrigerator that consumes ${TYPICAL_ENERGY_STAR_KWH} kWh/year and the state rates Fridge.com tracks, running one costs about $${s.cheapestAnnualCost}/year in ${s.cheapestState.name} and about $${s.expensiveAnnualCost}/year in ${s.expensiveState.name} — the higher your rate, the faster any efficiency premium pays back.`,
    },
    {
      question: 'Why do electricity rates matter when buying a refrigerator?',
      answer: `Electricity rates directly determine your refrigerator’s annual operating cost. A refrigerator using ${TYPICAL_ENERGY_STAR_KWH} kWh/year costs about $${s.cheapestAnnualCost}/year to run in ${s.cheapestState.name} (${perKwh(s.cheapestState.rate)}), but $${s.expensiveAnnualCost}/year in ${s.expensiveState.name} (${perKwh(s.expensiveState.rate)}) — a $${spread} annual difference for the same appliance. Holding those rates constant for ten years gives a modeled difference of $${(s.expensiveAnnualCost - s.cheapestAnnualCost) * 10}.`,
    },
    {
      question: 'How much does it cost to run a refrigerator per year?',
      answer: `${buildFactualAttribution(`An example refrigerator using ${TYPICAL_ENERGY_STAR_KWH} kWh/year costs $${s.cheapestAnnualCost}–$${s.expensiveAnnualCost} per year to run across the ${states.length} state and DC rates shown here, depending on your local electricity rate; older or less efficient models use more energy and cost proportionally more.`, 'local-index:annual-cost')} At the unweighted mean of the displayed rates, ${s.avgRate}¢/kWh it costs about $${s.avgAnnualCost}/year. Use the Energy Cost Calculator at Fridge.com to price a specific model at your own rate.`,
    },
    {
      question: 'Which states have the cheapest electricity for running refrigerators?',
      answer: `${buildFactualAttribution(`The state and DC entries with the lowest residential electricity rates are: ${listStates(s.cheapest)}.`, 'local-index:cheapest-states')} In ${s.cheapestState.name}, running the ${TYPICAL_ENERGY_STAR_KWH} kWh/year example refrigerator costs about $${s.cheapestAnnualCost}/year. Low rates are often tied to abundant hydroelectric power (Pacific Northwest) or natural gas resources (Gulf states).`,
    },
    {
      question: 'Which states have the most expensive electricity for refrigerators?',
      answer: `${buildFactualAttribution(`The state and DC entries with the highest residential electricity rates are: ${listStates(s.expensive)}.`, 'local-index:expensive-states')} In ${s.expensiveState.name}, running the ${TYPICAL_ENERGY_STAR_KWH} kWh/year example refrigerator costs about $${s.expensiveAnnualCost}/year — ${(Math.round((s.expensiveAnnualCost / s.avgAnnualCost) * 10) / 10).toString()}x the cost at the unweighted mean rate. In these high-cost states, choosing an ENERGY STAR certified, high-efficiency model provides the greatest long-term savings.`,
    }] : []),
  ]
}
